import React, { useState, useEffect, useMemo } from 'react';
import { UserX, AlertCircle, Users, Calendar, ChevronDown } from 'lucide-react';
import EmployeePhoto from './EmployeePhoto';
import ScrollToTop from './ScrollToTop';
import { api } from '../services/api';
import { formatDate } from '../utils/dateFormat';
import type { SkippedComparisonsTabProps, UnknownEmployee } from '../types';

const SkippedComparisonsTab: React.FC<SkippedComparisonsTabProps> = ({ rounds, currentRoundId }) => {
  const [unknownEmployees, setUnknownEmployees] = useState<UnknownEmployee[]>([]);
  const [loading, setLoading] = useState(false);

  // Default to current round (from active comparison), then active round, then latest completed, then first round
  const defaultRoundId = useMemo(() => {
    if (currentRoundId && rounds.some(r => r.roundID === currentRoundId)) return currentRoundId;

    const activeRound = rounds.find(r => r.status === 'active');
    if (activeRound) return activeRound.roundID;

    const completedRounds = rounds
      .filter(r => r.status === 'completed')
      .sort((a, b) => b.roundID - a.roundID);
    if (completedRounds.length > 0) return completedRounds[0].roundID;

    return rounds.length > 0 ? rounds[0].roundID : null;
  }, [rounds, currentRoundId]);

  const [selectedRoundId, setSelectedRoundId] = useState<number | null>(defaultRoundId);

  // Update default when rounds change
  useEffect(() => {
    setSelectedRoundId(defaultRoundId);
  }, [defaultRoundId]);

  // Fetch unknown employees when selected round changes
  useEffect(() => {
    if (!selectedRoundId) return;
    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      try {
        const data = await api.getUnknownEmployees(selectedRoundId);
        if (!cancelled) setUnknownEmployees(data);
      } catch (err) {
        console.error('Error fetching unknown employees:', err);
        if (!cancelled) setUnknownEmployees([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [selectedRoundId]);

  const selectedRound = selectedRoundId
    ? rounds.find(r => r.roundID === selectedRoundId)
    : null;

  const groupedByUser = unknownEmployees.reduce((acc, emp) => {
    const evaluatorName = emp.markedByUserName || 'Unknown User';
    if (!acc[evaluatorName]) {
      acc[evaluatorName] = [];
    }
    acc[evaluatorName].push(emp);
    return acc;
  }, {} as Record<string, UnknownEmployee[]>);

  return (
    <div className="space-y-6">
      {/* Responsive: header stacks on mobile */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2 sm:gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center shadow-lg flex-shrink-0">
              <UserX className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            Skipped Employees
          </h2>
        </div>

        <div className="relative w-full sm:w-auto">
          <select
            value={selectedRoundId ?? ''}
            onChange={(e) => setSelectedRoundId(Number(e.target.value))}
            className="appearance-none pl-4 pr-10 py-3 bg-white border-2 border-gray-200 rounded-lg text-sm font-medium text-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm cursor-pointer w-full sm:min-w-[220px] min-h-[44px]"
          >
            {rounds.map(round => (
              <option key={round.roundID} value={round.roundID}>
                {round.roundName} ({round.status})
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        </div>
      </div>

      {loading && (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-600 mx-auto mb-3"></div>
          <p className="text-gray-500 text-sm">Loading skipped employees...</p>
        </div>
      )}

      {!loading && unknownEmployees.length > 0 && (
        <div className="space-y-6">
          {Object.entries(groupedByUser).map(([evaluatorName, comparisons]) => (
            <div key={evaluatorName} className="bg-white rounded-xl shadow-lg p-4 sm:p-6 border border-gray-100">
              <div className="flex items-center gap-3 mb-4 pb-3 border-b-2 border-gray-200">
                <Users className="w-6 h-6 text-blue-600" />
                <h3 className="text-xl font-bold text-gray-900">{evaluatorName}</h3>
                <span className="ml-auto px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-semibold">
                  {comparisons.length} skipped
                </span>
              </div>

              <div className="space-y-3">
                {comparisons.map((emp, idx) => (
                  <div
                    key={emp.employeeID || idx}
                    className="p-4 bg-gradient-to-r from-orange-50 to-red-50 rounded-lg border border-orange-200 hover:shadow-md transition-shadow"
                  >
                    {/* Responsive: employee info stacks on small screens */}
                    <div className="flex flex-col xs:flex-row xs:items-center justify-between gap-2 xs:gap-4">
                      <div className="flex items-center gap-3 sm:gap-4">
                        <EmployeePhoto
                          photoURL={emp.employeePhoto}
                          name={emp.employeeName}
                          size="medium"
                        />
                        <div>
                          <p className="font-bold text-gray-900">{emp.employeeName}</p>
                          <p className="text-sm text-gray-600">{emp.employeeTitle}</p>
                          {emp.department && (
                            <p className="text-xs text-gray-500">{emp.department}</p>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-col items-end gap-2">
                        {emp.markedDate && (
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <Calendar className="w-4 h-4" />
                            {formatDate(emp.markedDate)}
                          </div>
                        )}
                        <span className="px-3 py-1 bg-orange-600 text-white rounded-full text-xs font-semibold">
                          Unknown Employee
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && unknownEmployees.length === 0 && (
        <div className="bg-white rounded-xl shadow-lg p-12 text-center border border-gray-100">
          <UserX className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">
            No Skipped Employees
          </h3>
          <p className="text-gray-600">
            {selectedRound
              ? 'No employees have been marked as unknown in this round yet'
              : 'Create an active round to start comparisons'}
          </p>
        </div>
      )}

      {!loading && unknownEmployees.length > 0 && (
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6 border border-blue-200">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
            <div>
              <h4 className="font-bold text-gray-900 mb-2">About Skipped Employees</h4>
              <p className="text-sm text-gray-700">
                These employees were marked as &quot;I Don&apos;t Know This Person&quot; by evaluators.
                They are excluded from all future comparison pairs for the evaluator who marked them.
                Skipped employees do not affect the Elo ratings.
              </p>
            </div>
          </div>
        </div>
      )}
      <ScrollToTop />
    </div>
  );
};

export default SkippedComparisonsTab;
